import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';
import { useMasters } from './useMasters';

interface Rate {
  id: string;
  metal_id: string;
  purity_id: string | null;
  rate_type: string;
  rate_per_gram: string | null;
  rate_per_fine_gram: string | null;
  effective_from: string;
  status: string;
}

export interface LatestRate {
  key: string;
  metalId: string;
  metalName: string;
  label: string;
  /** Per gram at that purity, or per fine gram when that is all that was published. */
  value: string;
  perFine: boolean;
  setAt: string;
  setToday: boolean;
}

/** True when a rate was published today — anything older is worth noticing. */
export function setOnDay(at: string): boolean {
  return new Date(at).toDateString() === new Date().toDateString();
}

/**
 * The newest approved selling rate for each purity — what the counter prices
 * from right now. Shared by the rate strip and the dashboard's rate board.
 */
export function useLatestRates(): { rates: LatestRate[]; isLoading: boolean } {
  const masters = useMasters();
  const { data, isLoading } = useQuery({
    queryKey: ['rates'],
    queryFn: () => endpoints.pricing.rates(),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  const all = (data?.data ?? []) as unknown as Rate[];
  const latest = new Map<string, Rate>();
  for (const rate of all.filter((r) => r.status === 'APPROVED' && r.rate_type === 'SALE')) {
    const key = `${rate.metal_id}:${rate.purity_id ?? 'any'}`;
    const held = latest.get(key);
    if (!held || rate.effective_from > held.effective_from) {
      latest.set(key, rate);
    }
  }

  const rates = [...latest.entries()]
    .map(([key, rate]) => {
      const metal = masters.metals.find((m) => m.id === rate.metal_id);
      const purity = masters.purities.find((p) => p.id === rate.purity_id);
      return {
        key,
        metalId: rate.metal_id,
        metalName: metal?.name ?? metal?.code ?? '',
        label: purity?.code ?? metal?.code ?? '',
        value: rate.rate_per_gram ?? rate.rate_per_fine_gram ?? '',
        perFine: !rate.rate_per_gram,
        setAt: rate.effective_from,
        setToday: setOnDay(rate.effective_from),
        order: masters.metals.findIndex((m) => m.id === rate.metal_id),
      };
    })
    .filter((r) => r.label && r.value)
    .sort((a, b) => a.order - b.order || b.label.localeCompare(a.label, 'en', { numeric: true }))
    .map(({ order: _order, ...r }) => r);

  return { rates, isLoading };
}
